import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  updateDoc,
  Timestamp,
  where,
  limit as firestoreLimit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '@/config/firebase';
import { ReceiptReview, ReviewQueueStats, FraudDetectionResult } from '@/models/receipt-review.model';
import { ReceiptStatus } from '@/models/receipt.model';
import {
  auditReceiptDecision,
  auditBulkOperation,
  auditView,
  auditListQuery,
} from '@/utils/audit-integration';
import { AuditEntityType, PHIDataType, AccessReason } from '@/models/audit-log.model';

const RECEIPTS_COLLECTION = 'receipts';
const BENEFIT_CATEGORIES_COLLECTION = 'benefitCategories';
const ADMIN_RECEIPTS_INDEX_COLLECTION = 'admin_index_receipts';

export class ReceiptReviewService {
  /**
   * Get review queue
   */
  static async getReviewQueue(): Promise<ReceiptReview[]> {
    const q = query(
      collection(db, ADMIN_RECEIPTS_INDEX_COLLECTION),
      where('statusCode', 'in', [0, 1]),
      orderBy('submittedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const reviews: ReceiptReview[] = [];
    for (const docSnap of snapshot.docs) {
      const review = await this.indexToReview(docSnap.id, docSnap.data());
      if (review) reviews.push(review);
    }

    // Sort by priority (requiresReview first, then duplicates, then by date)
    reviews.sort((a, b) => {
      if (a.requiresReview && !b.requiresReview) return -1;
      if (!a.requiresReview && b.requiresReview) return 1;
      if (a.isDuplicate && !b.isDuplicate) return -1;
      if (!a.isDuplicate && b.isDuplicate) return 1;
      if (a.fraudScore && a.fraudScore > 70) return -1;
      if (b.fraudScore && b.fraudScore > 70) return 1;
      return a.submittedAt.getTime() - b.submittedAt.getTime(); // Oldest first for fairness
    });

    auditListQuery(AuditEntityType.receipt, {
      queryName: 'review_queue',
      resultCount: reviews.length,
      filters: { statusCode: [0, 1] },
      containsPHI: true,
      phiDataTypes: [PHIDataType.billingRecord, PHIDataType.healthRecord],
      accessReason: AccessReason.payment,
    }).catch(console.error);

    return reviews;
  }

  /**
   * Get review queue statistics
   */
  static async getReviewQueueStats(): Promise<ReviewQueueStats> {
    const queue = await this.getReviewQueue();

    return {
      total: queue.length,
      pending: queue.filter(r => {
        if (typeof r.status === 'string') {
          return r.status === 'pending';
        }
        if (typeof r.status === 'number') {
          return r.status === 0; // 0 = pending
        }
        return false;
      }).length,
      inReview: queue.filter(r => {
        const status: any = r.status;
        if (typeof status === 'string') {
          return status === 'inReview' || status === 'in_review' || status === ReceiptStatus.inReview;
        }
        if (typeof status === 'number') {
          return status === 1; // 1 = inReview
        }
        return false;
      }).length,
      highPriority: queue.filter(r => r.requiresReview || (r.fraudScore && r.fraudScore > 70)).length,
      fraudFlagged: queue.filter(r => r.fraudScore && r.fraudScore > 50).length,
      duplicates: queue.filter(r => r.isDuplicate).length,
      processedToday: await this.getProcessedTodayCount(),
      avgProcessingTime: await this.getAvgProcessingTime(),
    };
  }

  /**
   * Get processed receipts (approved, rejected, paid)
   */
  static async getProcessedReceipts(limitCount: number = 50): Promise<ReceiptReview[]> {
    const q = query(
      collection(db, ADMIN_RECEIPTS_INDEX_COLLECTION),
      where('statusCode', 'in', [2, 3, 4]),
      orderBy('reviewedAt', 'desc'),
      firestoreLimit(limitCount)
    );

    const snapshot = await getDocs(q);

    const reviews: ReceiptReview[] = [];
    for (const docSnap of snapshot.docs) {
      const review = await this.indexToReview(docSnap.id, docSnap.data());
      if (review) reviews.push(review);
    }

    auditListQuery(AuditEntityType.receipt, {
      queryName: 'processed_receipts',
      resultCount: reviews.length,
      filters: { statusCode: [2, 3, 4] },
      containsPHI: true,
      phiDataTypes: [PHIDataType.billingRecord, PHIDataType.healthRecord],
      accessReason: AccessReason.payment,
    }).catch(console.error);

    return reviews;
  }

  private static async getProcessedTodayCount(): Promise<number> {
    // Very simple approximation - in production use aggregation queries
    // This fetches recent processed and counts today's
    const processed = await this.getProcessedReceipts(100);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return processed.filter(r => r.reviewedAt && r.reviewedAt >= today).length;
  }

  private static async getAvgProcessingTime(): Promise<number> {
    // Calculate avg processing time (hours) for recent 50 receipts
    const processed = await this.getProcessedReceipts(50);
    if (processed.length === 0) return 0;

    let totalHours = 0;
    let count = 0;

    for (const r of processed) {
      if (r.submittedAt && r.reviewedAt) {
        const diffMs = r.reviewedAt.getTime() - r.submittedAt.getTime();
        const diffHours = diffMs / (1000 * 60 * 60);
        totalHours += diffHours;
        count++;
      }
    }

    return count > 0 ? parseFloat((totalHours / count).toFixed(1)) : 0;
  }

  /**
   * Get receipt by ID for review
   */
  static async getReceiptForReview(receiptId: string): Promise<ReceiptReview | null> {
    const docRef = doc(db, RECEIPTS_COLLECTION, receiptId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    const receipt = { id: docSnap.id, ...docSnap.data() };
    const review = await this.receiptToReview(receipt);

    // Log PHI access when viewing receipt details
    if (review) {
      auditView(
        AuditEntityType.receipt,
        receiptId,
        `Receipt $${review.amount?.toFixed(2) || '0.00'}`,
        {
          containsPHI: true,
          phiDataTypes: [PHIDataType.billingRecord, PHIDataType.healthRecord],
          accessReason: AccessReason.payment,
          patientId: review.memberId,
        }
      ).catch(console.error);
    }

    return review;
  }

  /**
   * Detect fraud in receipt
   */
  static async detectFraud(receipt: ReceiptReview): Promise<FraudDetectionResult> {
    const flags: string[] = [];
    const reasons: string[] = [];
    let score = 0;

    // Check for duplicate
    if (receipt.isDuplicate) {
      score += 30;
      flags.push('duplicate');
      reasons.push('Receipt appears to be a duplicate');
    }

    // Check amount (unusually high amounts)
    if (receipt.amount > 10000) {
      score += 20;
      flags.push('high_amount');
      reasons.push('Unusually high amount');
    }

    // Check if requires review flag is set
    if (receipt.requiresReview) {
      score += 25;
      flags.push('requires_review');
      reasons.push('System flagged for manual review');
    }

    // Check for existing fraud flags
    if (receipt.fraudFlags && receipt.fraudFlags.length > 0) {
      score += receipt.fraudFlags.length * 10;
      flags.push(...receipt.fraudFlags);
      reasons.push(`System detected ${receipt.fraudFlags.length} fraud indicators`);
    }

    // Normalize score to 0-100
    score = Math.min(100, score);

    return {
      isFraud: score > 50,
      score,
      flags,
      reasons,
      confidence: score / 100,
    };
  }

  /**
   * Approve receipt
   */
  static async approveReceipt(
    receiptId: string,
    approvedAmount: number,
    reviewedBy: string,
    notes?: string,
    memberId?: string
  ): Promise<void> {
    // Call Cloud Function to handle full approval flow (creates ledger entry, queues payment)
    const functions = getFunctions();
    const approveReceiptFn = httpsCallable(functions, 'approveReceipt');

    try {
      await approveReceiptFn({
        receiptId,
        approvedAmount,
        reviewedBy,
        adminNotes: notes || null,
      });

      // Log receipt approval (PHI operation)
      const docRef = doc(db, RECEIPTS_COLLECTION, receiptId);
      const receipt = await getDoc(docRef);
      const actualMemberId = memberId || (receipt.exists() ? receipt.data().memberId : 'unknown');

      auditReceiptDecision(
        receiptId,
        'approved',
        actualMemberId,
        approvedAmount,
        notes
      ).catch(console.error);
    } catch (error) {
      console.error('Error approving receipt via Cloud Function:', error);
      throw error;
    }
  }

  /**
   * Reject receipt
   */
  static async rejectReceipt(
    receiptId: string,
    rejectionReason: string,
    reviewedBy: string,
    notes?: string,
    memberId?: string
  ): Promise<void> {
    const docRef = doc(db, RECEIPTS_COLLECTION, receiptId);

    // Get member ID if not provided
    let actualMemberId = memberId;
    if (!actualMemberId) {
      const receipt = await getDoc(docRef);
      if (receipt.exists()) {
        actualMemberId = receipt.data().memberId;
      }
    }

    await updateDoc(docRef, {
      status: ReceiptStatus.rejected,
      rejectionReason,
      reviewedBy,
      reviewedAt: Timestamp.now(),
      adminNotes: notes || null,
      lastUpdated: Timestamp.now(),
    });

    // Log receipt rejection (PHI operation)
    auditReceiptDecision(
      receiptId,
      'rejected',
      actualMemberId || 'unknown',
      undefined,
      rejectionReason
    ).catch(console.error);
  }

  /**
   * Bulk approve receipts
   */
  static async bulkApproveReceipts(
    receiptIds: string[],
    reviewedBy: string
  ): Promise<void> {
    void receiptIds;
    void reviewedBy;
    throw new Error(
      'Bulk approval is disabled. Approve receipts individually to ensure ledger and payment integrity.'
    );
  }

  /**
   * Convert receipt document to ReceiptReview
   */
  private static async receiptToReview(receipt: any): Promise<ReceiptReview | null> {
    try {
      // Get member info (not used but kept for potential future use)
      // let memberName = 'Unknown';
      // try {
      //   const memberDoc = await getDoc(doc(db, MEMBERS_COLLECTION, receipt.memberId));
      //   if (memberDoc.exists()) {
      //     const memberData = memberDoc.data();
      //     memberName = memberData.firstName && memberData.lastName
      //       ? `${memberData.firstName} ${memberData.lastName}`
      //       : memberData.email || 'Unknown';
      //   }
      // } catch (e) {
      //   console.error('Error fetching member:', e);
      // }

      // Get category name
      let categoryName: string | undefined;
      if (receipt.categoryId) {
        try {
          const categoryDoc = await getDoc(doc(db, BENEFIT_CATEGORIES_COLLECTION, receipt.categoryId));
          if (categoryDoc.exists()) {
            categoryName = categoryDoc.data().name;
          }
        } catch (e) {
          console.error('Error fetching category:', e);
        }
      }

      // Determine status - handle both string and number statuses
      let status: ReceiptStatus | string;
      if (typeof receipt.status === 'string') {
        status = receipt.status;
      } else if (typeof receipt.status === 'number') {
        // Map number to string status
        const statusMap: Record<number, string> = {
          0: 'pending',
          1: 'in_review',
          2: 'approved',
          3: 'rejected',
          4: 'paid',
        };
        status = statusMap[receipt.status] || 'pending';
      } else {
        status = 'pending';
      }

      return {
        receiptId: receipt.id,
        memberId: receipt.memberId,
        employerId: receipt.employerId,
        amount: receipt.amount || 0,
        categoryId: receipt.categoryId,
        categoryName,
        providerName: receipt.providerName,
        serviceDate: receipt.serviceDate?.toDate(),
        status: status as ReceiptStatus,
        requiresReview: receipt.requiresReview || false,
        isDuplicate: receipt.isDuplicate || false,
        fraudScore: receipt.fraudScore,
        fraudFlags: receipt.fraudFlags || [],
        submittedAt: receipt.createdAt?.toDate() || new Date(),
        reviewedAt: receipt.reviewedAt?.toDate(),
        reviewedBy: receipt.reviewedBy,
        reviewedByName: receipt.reviewedByName,
        notes: receipt.adminNotes,
        receiptImageUrl: receipt.receiptImageUrl,
        ocrText: receipt.ocrText,
      };
    } catch (error) {
      console.error('Error converting receipt to review:', error);
      return null;
    }
  }

  private static async indexToReview(
    receiptId: string,
    indexData: any
  ): Promise<ReceiptReview | null> {
    try {
      let categoryName: string | undefined;
      if (indexData.categoryId) {
        try {
          const categoryDoc = await getDoc(doc(db, BENEFIT_CATEGORIES_COLLECTION, indexData.categoryId));
          if (categoryDoc.exists()) {
            categoryName = categoryDoc.data().name;
          }
        } catch (e) {
          console.error('Error fetching category:', e);
        }
      }

      return {
        receiptId,
        memberId: indexData.memberId,
        employerId: indexData.employerId,
        amount: indexData.amount || 0,
        categoryId: indexData.categoryId || undefined,
        categoryName,
        providerName: indexData.providerName || undefined,
        serviceDate: indexData.serviceDate?.toDate?.(),
        status: indexData.statusText as ReceiptStatus,
        requiresReview: indexData.requiresReview || false,
        isDuplicate: indexData.isDuplicate || false,
        fraudScore: indexData.fraudScore || undefined,
        fraudFlags: indexData.fraudFlags || [],
        submittedAt: indexData.submittedAt?.toDate?.() || new Date(),
        reviewedAt: indexData.reviewedAt?.toDate?.(),
        reviewedBy: indexData.reviewedBy || undefined,
        reviewedByName: indexData.reviewedByName || undefined,
        receiptImageUrl: indexData.receiptImageUrl || undefined,
      };
    } catch (error) {
      console.error('Error converting receipt index to review:', error);
      return null;
    }
  }
}

